import React, { useEffect, useState } from 'react';
import { useToast, Toast, ToastType } from '../../contexts/ToastContext';
// ── Icons ────────────────────────────────────────────────────────────────────
function IconSuccess() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6512" data-mp="37816ohobkwnx">
      
      <path d="M20 6 9 17l-5-5" data-id="element-6513" data-mp="3gyvtbn5h9y9w" />
    </svg>;
}
function IconError() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6514" data-mp="3gym2xygitp3o">
      
      <circle cx="12" cy="12" r="10" data-id="element-6515" data-mp="3pzai4whnhvi5" />
      <line x1="15" y1="9" x2="9" y2="15" data-id="element-6516" data-mp="3q0d4qs7xxbs9" />
      <line x1="9" y1="9" x2="15" y2="15" data-id="element-6517" data-mp="3uyes3pcefx9j" />
    </svg>;
}
function IconWarning() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6518" data-mp="1vlj0b9p645bm">
      
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" data-id="element-6519" data-mp="20l5lq6f5grvi" />
      <line x1="12" y1="9" x2="12" y2="13" data-id="element-6520" data-mp="lgzil26vxpwj" />
      <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-6521" data-mp="gp7z1bm5ckfw" />
    </svg>;
}
function IconInfo() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-6522" data-mp="bpv7idkbyp6z">
      
      <circle cx="12" cy="12" r="10" data-id="element-6523" data-mp="7pusaqpeq9yv" />
      <line x1="12" y1="8" x2="12" y2="12" data-id="element-6524" data-mp="14ek6a5xwyifk" />
      <line x1="12" y1="16" x2="12.01" y2="16" data-id="element-6525" data-mp="14d96sibi3wu9" />
    </svg>;
}
// ── Config per type ──────────────────────────────────────────────────────────
const CONFIG: Record<ToastType, {
  icon: React.ReactNode;
  classes: string;
  bar: string;
}> = {
  success: {
    icon: <IconSuccess data-id="element-6526" data-mp="qf1bsfy0z5lq" />,
    classes: 'bg-white dark:bg-gray-800 border-l-4 border-emerald-500 text-emerald-700 dark:text-emerald-400',
    bar: 'bg-emerald-500'
  },
  error: {
    icon: <IconError data-id="element-6527" data-mp="3g1qncd9ouhsv" />,
    classes: 'bg-white dark:bg-gray-800 border-l-4 border-red-500 text-red-700 dark:text-red-400',
    bar: 'bg-red-500'
  },
  warning: {
    icon: <IconWarning data-id="element-6528" data-mp="3cngwbmhofmpo" />,
    classes: 'bg-white dark:bg-gray-800 border-l-4 border-amber-500 text-amber-700 dark:text-amber-400',
    bar: 'bg-amber-500'
  },
  info: {
    icon: <IconInfo data-id="element-6529" data-mp="33m91xfia760v" />,
    classes: 'bg-white dark:bg-gray-800 border-l-4 border-teal-500 text-teal-700 dark:text-teal-400',
    bar: 'bg-teal-500'
  }
};
// ── Single toast item ────────────────────────────────────────────────────────
function ToastItem({
  toast,
  onDismiss
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const duration = toast.duration ?? 3500;
  const cfg = CONFIG[toast.type];
  // Mount animation
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);
  const handleDismiss = () => {
    setLeaving(true);
    setTimeout(() => onDismiss(toast.id), 300);
  };
  return <div role="alert" onClick={handleDismiss} style={{
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    transform: visible && !leaving ? 'translateX(0) scale(1)' : 'translateX(110%) scale(0.92)',
    opacity: visible && !leaving ? 1 : 0
  }} className={`
        relative flex items-start gap-3 w-80 max-w-[calc(100vw-2rem)]
        rounded-xl shadow-lg shadow-black/10 dark:shadow-black/30
        px-4 py-3 cursor-pointer select-none
        overflow-hidden
        ${cfg.classes}
      `} data-id="element-6530" data-mp="2fxziulcgcwdf">
      
      {/* Icon */}
      <span className="mt-0.5 shrink-0" data-id="element-6531" data-mp="2aae926bvhwui">{cfg.icon}</span>

      {/* Text */}
      <div className="flex-1 min-w-0" data-id="element-6532" data-mp="2oysjb2bmcnl3">
        <p className="font-semibold text-sm leading-snug text-gray-900 dark:text-white" data-id="element-6533" data-mp="2oxqulv1ok2kw">
          {toast.title}
        </p>
        {toast.message && <p className="text-xs mt-0.5 text-gray-500 dark:text-gray-400 leading-relaxed" data-id="element-6534" data-mp="2k1sbomz4fcrv">
            {toast.message}
          </p>}
      </div>

      {/* Close button */}
      <button onClick={e => {
      e.stopPropagation();
      handleDismiss();
    }} className="shrink-0 mt-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors" aria-label="Cerrar" data-id="element-6535" data-mp="2t1oysk5v43kt">
        
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" data-id="element-6536" data-mp="w86npun294j7">
          
          <line x1="18" y1="6" x2="6" y2="18" data-id="element-6537" data-mp="1534ofnwg9ll3" />
          <line x1="6" y1="6" x2="18" y2="18" data-id="element-6538" data-mp="151tfzas27pnn" />
        </svg>
      </button>

      {/* Progress bar */}
      <span className={`absolute bottom-0 left-0 h-0.5 ${cfg.bar} opacity-60`} style={{
      animation: `toast-progress ${duration}ms linear forwards`
    }} data-id="element-6539" data-mp="8ef3tptfuevf" />
      

      <style data-id="element-6540" data-mp="hc8xesztiuew">{`
        @keyframes toast-progress {
          from { width: 100%; }
          to   { width: 0%;   }
        }
      `}</style>
    </div>;
}
// ── Container ────────────────────────────────────────────────────────────────
export function ToastContainer() {
  const {
    toasts,
    dismissToast
  } = useToast();
  return <div aria-live="polite" className="fixed bottom-5 right-5 z-[99999] flex flex-col gap-2 items-end pointer-events-none" data-id="element-6541" data-mp="1xtd5fcf91zy3">
      
      {toasts.map(t => <div key={t.id} className="pointer-events-auto" data-id="element-6542" data-mp="22vuhiut2ui4f">
          <ToastItem toast={t} onDismiss={dismissToast} data-id="element-6543" data-mp="22ygootc22vm4" />
        </div>)}
    </div>;
}